// Shared, de-duped cache for the "all web layouts" fetch.
//
// Both the footer shuffle controls (ShuffleLayoutControls) and the Layouts
// sidebar tab (LayoutsAction.fetchAllLayouts) need the full list of layouts to
// compute the max image count / find compatible layouts for shuffling. They
// used to fetch it independently (and the footer fetched page + spread as two
// separate calls), producing several redundant getLayouts requests on load.
//
// This module fetches ONCE, de-dupes concurrent callers via an in-flight
// promise, and hands back the RAW (decompressed but UNSCALED) layouts split
// into page / spread. Callers scale on their own (scaleLayout mutates in place,
// so each caller must clone before scaling — never mutate these cached items).

import { apiPost } from "../common-services/apiCall.js";
import { ENDPOINTS } from "../constants/apiurl.js";
import { EDITOR_ASSETS } from "../constants/index.js";
import { decompressFromBase64 } from "../common-functions/index.js";

const BIG_LIMIT = 10000000;

// Reads the Auto Layout tick off a layout. Used by Asset Management to render
// the pill; the POOLS are decided server-side by `filter.auto_layout`, not here.
export const isAutoLayout = (layout) => layout?.auto_layout === true;

// Two independent caches over the same endpoint:
//   full -> no auto_layout key sent, so the API returns EVERYTHING. Shuffle,
//           the browse grid and blank-theme generation use this.
//   auto -> `filter.auto_layout: true`, so the API returns only the ticked
//           layouts. Auto Layout is a SUBSET of the full catalog, not a
//           complement — a ticked layout still comes up in a shuffle.
let cache = null;
let inflight = null;
let autoCache = null;
let autoInflight = null;

const layoutsRequest = (extraFilter) => ({
  filter: {
    status: { $in: [1, 3] },
    display_in_web: true,
    number_of_images: { $gte: 1 },
    asset_type: EDITOR_ASSETS.LAYOUT,
    ...extraFilter,
  },
  skip: 0,
  limit: BIG_LIMIT,
  sortField: "_id",
  sortOrder: "desc",
});

// One combined query for both page and spread layouts — the union is identical
// to the previous two calls (number_of_layouts 1 vs 2). Split client-side.
const fetchAndSplit = (extraFilter) =>
  apiPost(ENDPOINTS.getLayouts, layoutsRequest(extraFilter)).then((res) => {
    const items = (res?.items || []).map((it) => ({
      ...it,
      layout: "",
      layout_c: decompressFromBase64(it.layout_c),
    }));
    return {
      page: items.filter((it) => !it.spread && it.number_of_layouts === 1),
      spread: items.filter((it) => it.spread && it.number_of_layouts === 2),
      all: items,
    };
  });

export const fetchAllLayoutsCached = () => {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;

  inflight = fetchAndSplit()
    .then((result) => {
      cache = result;
      inflight = null;
      return cache;
    })
    .catch((err) => {
      inflight = null; // allow a retry on the next call
      throw err;
    });

  return inflight;
};

// Only the layouts ticked for Auto Layout — `filter.auto_layout: true`.
// Separate cache from the full fetch: different query, and the generator is the
// only consumer.
export const fetchAutoLayoutsCached = () => {
  if (autoCache) return Promise.resolve(autoCache);
  if (autoInflight) return autoInflight;

  autoInflight = fetchAndSplit({ auto_layout: true })
    .then((result) => {
      autoCache = result;
      autoInflight = null;
      return autoCache;
    })
    .catch((err) => {
      autoInflight = null;
      throw err;
    });

  return autoInflight;
};

// Bust both caches after a layout is created/deleted or its Auto Layout tick
// changes, so the next fetch re-reads the authoritative lists.
export const invalidateLayoutsCache = () => {
  cache = null;
  inflight = null;
  autoCache = null;
  autoInflight = null;
};
